import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCRM } from '@/hooks/useCRM';
import { useAuth } from '@/context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { CommunicationHub } from '@/components/crm/CommunicationHub';
import { CRMPageHeader } from '@/components/crm/CRMPageHeader';
import { FlowInlineLauncher } from '@/components/flow/FlowWidget';
import {
  Plus,
  Search,
  X,
  Save,
  Edit2,
  Building2,
  GripVertical,
  Trash2,
  CheckSquare,
  ChevronDown,
  ArrowRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Phone,
  Linkedin,
  LayoutGrid,
  List,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Contact, ContactStage } from '@/types/contacts';

const STAGES: { id: ContactStage; label: string; color: string }[] = [
  { id: 'new', label: 'New', color: '#3B82F6' },
  { id: 'qualified', label: 'Qualified', color: '#0EA5E9' },
  { id: 'proposal', label: 'Proposal', color: '#F59E0B' },
  { id: 'negotiation', label: 'Negotiation', color: '#A855F7' },
  { id: 'closed_won', label: 'Won', color: '#10B981' },
  { id: 'closed_lost', label: 'Lost', color: '#EF4444' },
];

const STAGE_ORDER: ContactStage[] = ['new', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

const STAGE_BADGE_STYLES: Record<ContactStage, string> = {
  new: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/50 dark:text-slate-300 dark:border-slate-800',
  qualified: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800',
  proposal: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  negotiation: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800',
  closed_won: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  closed_lost: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
};

type ListSortField = 'name' | 'company' | 'stage' | 'value';

export function CRMContacts() {
  const location = useLocation();
  const { contacts, refresh } = useCRM();
  const { token } = useAuth();
  const { toast } = useToast();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const stageFilter = queryParams.get('stage') as ContactStage | null;
  const initialView = queryParams.get('view') === 'list' ? 'list' : 'kanban';

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', company: '', value: '' });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>(initialView);
  const [detailContact, setDetailContact] = useState<Contact | null>(null);
  const [listSortField, setListSortField] = useState<ListSortField>('stage');
  const [listSortDirection, setListSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setViewMode(queryParams.get('view') === 'list' ? 'list' : 'kanban');
  }, [queryParams]);

  useEffect(() => {
    if (viewMode !== 'kanban' && selectMode) {
      setSelectMode(false);
    }
  }, [viewMode, selectMode]);

  // Filter contacts
  const filtered = useMemo(() => {
    return contacts.filter(c => {
      const matchesSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase()) ||
        c.company?.toLowerCase().includes(search.toLowerCase());

      const matchesStage = !stageFilter || c.stage === stageFilter;
      return matchesSearch && matchesStage;
    });
  }, [contacts, search, stageFilter]);

  // Keep selection stable and predictable when filters/search change
  useEffect(() => {
    const visibleIds = new Set(filtered.map((c) => c.id));
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [filtered]);

  const selectedContacts = filtered.filter((c) => selectedIds.has(c.id));
  const showSelectionActions = selectedIds.size > 0;
  const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
  const someVisibleSelected = filtered.some((c) => selectedIds.has(c.id));

  const sortedContacts = useMemo(() => {
    const contactsToSort = [...filtered];

    contactsToSort.sort((a, b) => {
      let comparison = 0;

      if (listSortField === 'stage') {
        comparison = STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage);
      } else if (listSortField === 'value') {
        comparison = (a.value || 0) - (b.value || 0);
      } else {
        comparison = (a[listSortField] || '').toString().localeCompare((b[listSortField] || '').toString());
      }

      if (comparison === 0) {
        comparison = a.name.localeCompare(b.name);
      }

      return listSortDirection === 'asc' ? comparison : -comparison;
    });

    return contactsToSort;
  }, [filtered, listSortDirection, listSortField]);

  const currentSortLabel = listSortField === 'stage'
    ? 'Stage'
    : listSortField === 'company'
      ? 'Company'
      : listSortField === 'value'
        ? 'Value'
        : 'Name';

  const toLinkedinUrl = (value?: string) => {
    if (!value) return null;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `https://${value}`;
  };

  const emailSelectedContacts = () => {
    if (selectedContacts.length === 0) return;
    const recipients = selectedContacts.map((c) => c.email).filter(Boolean);
    if (recipients.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No emails found',
        description: 'Selected contacts do not have valid email addresses.',
      });
      return;
    }

    const subject = encodeURIComponent('Quick intro from RaiseBeacon');
    window.location.href = `mailto:${recipients.join(',')}?subject=${subject}`;
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelected = (id: string) => {
    if (!selectMode) {
      setSelectMode(true);
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    if (!selectMode) {
      setSelectMode(true);
    }
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filtered.map((c) => c.id)));
  };

  const bulkUpdateStage = async (stage: ContactStage) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/crm/contacts/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'update_stage', ids: Array.from(selectedIds), stage }),
      });
      if (res.ok) {
        const label = STAGES.find(s => s.id === stage)?.label ?? stage;
        toast({ title: `Moved ${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'} to ${label}` });
        exitSelectMode();
        await refresh();
      } else {
        toast({ variant: 'destructive', title: 'Failed to move contacts' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error' });
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/crm/contacts/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'delete', ids: Array.from(selectedIds) }),
      });
      if (res.ok) {
        toast({ title: `Removed ${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'}` });
        setConfirmBulkDelete(false);
        exitSelectMode();
        await refresh();
      } else {
        toast({ variant: 'destructive', title: 'Failed to remove contacts' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error' });
    }
  };

  const startEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setEditForm({
      name: contact.name || '',
      email: contact.email || '',
      company: contact.company || '',
      phone: contact.phone || '',
      value: contact.value?.toString() || '',
      stage: contact.stage,
      tags: (contact.tags || []).join(', '),
    });
  };

  const openContactDetail = (contact: Contact) => {
    setDetailContact(contact);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/crm/contacts/${editingId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          email: editForm.email,
          company: editForm.company || undefined,
          phone: editForm.phone || undefined,
          value: editForm.value ? parseInt(editForm.value) : undefined,
          stage: editForm.stage,
          tags: editForm.tags ? editForm.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : undefined,
        }),
      });
      
      if (res.ok) {
        toast({ title: 'Saved' });
        setEditingId(null);
        await refresh(); // Reload all contacts
      } else {
        toast({ variant: 'destructive', title: 'Failed to save' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error' });
    }
  };

  const deleteContact = async (contactId: string = editingId as string) => {
    if (!contactId) return;

    const contact = contacts.find((c) => c.id === contactId);
    if (!contact || !window.confirm(`Are you sure you want to remove "${contact.name}" from your network? This action cannot be undone.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/crm/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (res.ok) {
        toast({ title: 'Contact removed' });
        setEditingId(null);
        if (detailContact?.id === contactId) {
          setDetailContact(null);
        }
        await refresh();
      } else {
        toast({ variant: 'destructive', title: 'Failed to remove contact' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error' });
    }
  };


  const createContact = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newContact.name,
          email: newContact.email,
          company: newContact.company || undefined,
          value: newContact.value ? parseInt(newContact.value) : undefined,
          stage: 'new',
        }),
      });
      
      if (res.ok) {
        toast({ title: 'Contact created' });
        setShowAdd(false);
        setNewContact({ name: '', email: '', company: '', value: '' });
        await refresh();
      } else {
        const err = await res.json();
        toast({ variant: 'destructive', title: err.error || 'Failed to create' });
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error' });
    }
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header */}
      <CRMPageHeader
        title="Contacts"
        description="Manage investor pipeline, outreach, and stage progression"
        actions={(
          <>
            <FlowInlineLauncher className="shrink-0" />
            <div className="relative min-w-[200px] flex-1 max-w-sm sm:min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center rounded-lg border bg-muted/40 p-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setViewMode('list')}
                className={cn(
                  'min-w-[96px] transition-all duration-250',
                  viewMode === 'list'
                    ? 'bg-[#2B7FFF] text-white shadow-sm hover:bg-[#2B7FFF]/90'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <List className="h-4 w-4 mr-1" />
                List
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setViewMode('kanban')}
                className={cn(
                  'min-w-[96px] transition-all duration-250',
                  viewMode === 'kanban'
                    ? 'bg-[#2B7FFF] text-white shadow-sm hover:bg-[#2B7FFF]/90'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <LayoutGrid className="h-4 w-4 mr-1" />
                Pipeline
              </Button>
            </div>
            <Button onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
          </>
        )}
      />

      {viewMode === 'kanban' && (
        <div className="rounded-xl border bg-card p-3 shadow-sm">
          <div className="flex items-center gap-2">
            {!selectMode ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setSelectMode(true)}>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  Select Cards
                </Button>
                <span className="text-xs text-muted-foreground">
                  Enable multi-select, then click cards to select them.
                </span>
              </>
            ) : (
              <>
                <span className="rounded bg-[#2B7FFF]/10 px-2 py-1 text-xs font-medium text-[#2B7FFF]">
                  Selection mode active
                </span>
                <span className="text-xs text-muted-foreground">
                  Click cards to select. Actions appear once at least one card is selected.
                </span>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={exitSelectMode}>
                  <X className="h-4 w-4 mr-1" />
                  Exit Selection
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className={cn(
          'overflow-hidden rounded-xl border bg-[#2B7FFF]/5 transition-all duration-300 ease-out',
          showSelectionActions ? 'max-h-24 opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-1 border-transparent'
        )}
      >
        <div className="flex items-center gap-2 p-2">
          <span className="rounded bg-[#2B7FFF]/10 px-2 py-1 text-xs font-medium text-[#2B7FFF]">
            {selectedIds.size} selected
          </span>
          <Button variant="outline" size="sm" disabled={selectedIds.size === 0} onClick={emailSelectedContacts}>
            <Mail className="h-4 w-4 mr-1" />
            Email
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={selectedIds.size === 0}>
                <ArrowRight className="h-4 w-4 mr-1" />
                Move to
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {STAGES.map(s => (
                <DropdownMenuItem key={s.id} onClick={() => bulkUpdateStage(s.id)}>
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedIds.size === 0}
            onClick={() => setConfirmBulkDelete(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={exitSelectMode}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {stageFilter && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Filtered to stage:</span>
          <Badge variant="secondary">{STAGES.find((s) => s.id === stageFilter)?.label || stageFilter}</Badge>
        </div>
      )}

      <div className="rounded-xl border bg-[#2B7FFF]/5 px-4 py-3 text-sm text-muted-foreground">
        Outreach shortcut: switch to List view, select one or more contacts, then click Email to start a message with real contact emails.
      </div>

      <AnimatePresence mode="wait" initial={false}>
      {viewMode === 'list' ? (
        <motion.div
          key="list-view"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="flex-1 min-h-0 overflow-hidden rounded-2xl border bg-card"
        >
          <div className="border-b bg-muted/40 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={(checked) => toggleSelectAllVisible(Boolean(checked))}
                  aria-label={someVisibleSelected && !allVisibleSelected ? 'Some contacts selected' : 'Select all contacts'}
                />
                <span className="text-xs font-medium text-muted-foreground">Select all</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                <select
                  value={listSortField}
                  onChange={(e) => setListSortField(e.target.value as ListSortField)}
                  className="h-7 bg-transparent text-xs font-medium outline-none"
                  aria-label="Sort contacts by"
                >
                  <option value="stage">Stage</option>
                  <option value="name">Name</option>
                  <option value="company">Company</option>
                  <option value="value">Value</option>
                </select>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setListSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))}
                  aria-label={`Sort ${currentSortLabel.toLowerCase()} ${listSortDirection === 'asc' ? 'descending' : 'ascending'}`}
                >
                  {listSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                </Button>
              </div>
              <span className="text-xs text-muted-foreground">
                Sorted by {currentSortLabel} ({listSortDirection})
              </span>
            </div>
            <div className="mt-3 grid grid-cols-[36px_1.8fr_1.4fr_1fr_1fr_220px] gap-2 text-xs font-medium text-muted-foreground">
              <span className="text-center">Sel</span>
              <span>Contact</span>
              <span>Company</span>
              <span>Stage</span>
              <span>Value</span>
              <span>Quick Actions</span>
            </div>
          </div>
          <ScrollArea className="h-full">
            <div className="divide-y">
              {sortedContacts.map((contact) => {
                const linkedinUrl = toLinkedinUrl(contact.linkedin);
                return (
                  <div
                    key={contact.id}
                    className={cn(
                      'grid grid-cols-[36px_1.8fr_1.4fr_1fr_1fr_220px] gap-2 items-center px-3 py-2 transition-colors duration-200',
                      selectedIds.has(contact.id) ? 'bg-[#2B7FFF]/5' : 'hover:bg-muted/40'
                    )}
                  >
                    <Checkbox
                      checked={selectedIds.has(contact.id)}
                      onCheckedChange={() => toggleSelected(contact.id)}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{contact.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                    </div>
                    <p className="text-sm truncate text-muted-foreground">{contact.company || 'No company'}</p>
                    <Badge variant="outline" className={cn('w-fit text-[10px] font-medium', STAGE_BADGE_STYLES[contact.stage])}>
                      {STAGES.find((s) => s.id === contact.stage)?.label || contact.stage}
                    </Badge>
                    <p className="text-sm">{contact.value ? `$${contact.value.toLocaleString()}` : '-'}</p>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" asChild>
                        <a href={`mailto:${contact.email}`} aria-label={`Email ${contact.name}`}>
                          <Mail className="h-4 w-4" />
                        </a>
                      </Button>
                      {contact.phone ? (
                        <Button size="icon" variant="ghost" asChild>
                          <a href={`tel:${contact.phone}`} aria-label={`Call ${contact.name}`}>
                            <Phone className="h-4 w-4" />
                          </a>
                        </Button>
                      ) : (
                        <Button size="icon" variant="ghost" disabled>
                          <Phone className="h-4 w-4" />
                        </Button>
                      )}
                      {linkedinUrl ? (
                        <Button size="icon" variant="ghost" asChild>
                          <a href={linkedinUrl} target="_blank" rel="noreferrer" aria-label={`Open LinkedIn for ${contact.name}`}>
                            <Linkedin className="h-4 w-4" />
                          </a>
                        </Button>
                      ) : (
                        <Button size="icon" variant="ghost" disabled>
                          <Linkedin className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openContactDetail(contact)}>
                        Open
                      </Button>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                  No contacts match your filters.
                </div>
              )}
            </div>
          </ScrollArea>
        </motion.div>
      ) : (
      <motion.div
        key="pipeline-view"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="flex-1 flex gap-4 overflow-x-auto min-h-0 pb-1"
      >
        {STAGES.map(stage => {
          const stageContacts = filtered.filter(c => c.stage === stage.id);
          const totalValue = stageContacts.reduce((sum, c) => sum + (c.value || 0), 0);
          
          return (
            <div key={stage.id} className="w-[300px] flex-shrink-0 flex flex-col overflow-hidden rounded-xl border bg-muted/20 shadow-sm">
              {/* Column Header */}
              <div className="border-b bg-background/80 px-3 py-3 backdrop-blur-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                    <span className="font-semibold text-sm truncate">{stage.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs shrink-0">
                    {stageContacts.length}
                  </Badge>
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{stageContacts.length} contact{stageContacts.length === 1 ? '' : 's'}</span>
                  {totalValue > 0 ? (
                    <span className="font-medium text-[#2B7FFF]">${totalValue.toLocaleString()}</span>
                  ) : (
                    <span className="font-medium">No value yet</span>
                  )}
                </div>
              </div>
              
              {/* Cards */}
              <ScrollArea 
                className={cn(
                  'flex-1 min-h-0 px-2 py-2',
                  dragOverStage === stage.id && 'bg-[#2B7FFF]/10'
                )}
                onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggingId) {
                    // Update contact stage
                    fetch(`${API_URL}/api/crm/contacts/${draggingId}`, {
                      method: 'PUT',
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ stage: stage.id }),
                    }).then(() => refresh());
                    setDraggingId(null);
                    setDragOverStage(null);
                  }
                }}
              >
                <div className="space-y-2 w-full pb-2">
                  {stageContacts.map(contact => {
                    const isSelected = selectedIds.has(contact.id);
                    return (
                    <div
                      key={contact.id}
                      draggable={!selectMode}
                      onDragStart={() => setDraggingId(contact.id)}
                      onDragEnd={() => { setDraggingId(null); setDragOverStage(null); }}
                      className={cn(
                        'w-full rounded-xl border bg-card p-3.5 cursor-pointer transition-all shadow-sm',
                        'hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-[#2B7FFF]/40 hover:border-[#2B7FFF]/30',
                        draggingId === contact.id && 'opacity-50 ring-2 ring-[#2B7FFF]',
                        selectMode && isSelected && 'ring-2 ring-[#2B7FFF] border-[#2B7FFF] bg-[#2B7FFF]/5'
                      )}
                      onClick={() => {
                        if (selectMode) toggleSelected(contact.id);
                        else startEdit(contact);
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {selectMode && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelected(contact.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="font-medium text-sm truncate leading-tight">{contact.name}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{contact.title || 'No title'}</p>
                          {contact.company && (
                            <p className="text-[10px] text-muted-foreground flex items-center gap-0.5 mt-1">
                              <Building2 className="h-2.5 w-2.5 flex-shrink-0" />
                              <span className="truncate">{contact.company}</span>
                            </p>
                          )}
                        </div>
                        {!selectMode && (
                          <div className="flex items-center gap-0.5 flex-shrink-0 rounded-md bg-muted/40 p-0.5">
                            <div
                              className="p-1 text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <GripVertical className="h-3.5 w-3.5" />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); startEdit(contact); }}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 flex items-center justify-between gap-2 border-t pt-2">
                        <Badge variant="outline" className={cn('text-[10px] font-medium', STAGE_BADGE_STYLES[contact.stage])}>
                          {contact.stage}
                        </Badge>
                        {contact.value && contact.value > 0 && (
                          <span className="text-[10px] font-medium text-green-600">
                            ${contact.value.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          );
        })}
      </motion.div>
      )}
      </AnimatePresence>

      {/* Edit Modal */}
      <Dialog open={!!editingId} onOpenChange={() => setEditingId(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={editForm.company} onChange={e => setEditForm({...editForm, company: e.target.value})} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
            </div>
            <div>
              <Label>Deal Value ($)</Label>
              <Input 
                type="number" 
                value={editForm.value} 
                onChange={e => setEditForm({...editForm, value: e.target.value})} 
              />
            </div>
            <div className="mb-1">
              <Label className="mb-1.5 block">Stage</Label>
              <select 
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                value={editForm.stage}
                onChange={e => setEditForm({...editForm, stage: e.target.value})}
              >
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div className="mt-2">
              <Label className="mb-1.5 block">Tags (comma separated)</Label>
              <Input value={editForm.tags} onChange={e => setEditForm({...editForm, tags: e.target.value})} />
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="destructive" onClick={deleteContact}>
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setEditingId(null)}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button className="bg-[#2B7FFF]" onClick={saveEdit}>
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailContact} onOpenChange={(open: boolean) => !open && setDetailContact(null)}>
        <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden flex flex-col">
          {detailContact && (
            <>
              <div className="flex items-start justify-between gap-4 border-b bg-background px-6 py-4">
                <div className="min-w-0">
                  <DialogTitle className="truncate text-xl">{detailContact.name}</DialogTitle>
                  <p className="truncate text-sm text-muted-foreground">
                    {detailContact.company || 'No company'}{detailContact.title ? ` • ${detailContact.title}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <Badge variant="secondary" className={cn('text-xs font-medium', STAGE_BADGE_STYLES[detailContact.stage])}>
                    {STAGES.find((stage) => stage.id === detailContact.stage)?.label || detailContact.stage}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => startEdit(detailContact)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteContact(detailContact.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <CommunicationHub contact={detailContact} onRefresh={refresh} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newContact.name} 
                onChange={e => setNewContact({...newContact, name: e.target.value})} 
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                value={newContact.email} 
                onChange={e => setNewContact({...newContact, email: e.target.value})} 
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input 
                value={newContact.company} 
                onChange={e => setNewContact({...newContact, company: e.target.value})} 
              />
            </div>
            <div>
              <Label>Deal Value ($)</Label>
              <Input 
                type="number"
                value={newContact.value} 
                onChange={e => setNewContact({...newContact, value: e.target.value})} 
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#2B7FFF]" 
                onClick={createContact}
                disabled={!newContact.name || !newContact.email}
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedIds.size} contact{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected contacts from your network. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CRMContacts;
